import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useInView } from "../../hooks/useInView";
import { EnvelopeIcon, PhoneIcon, UserIcon } from "@heroicons/react/24/outline";
import StripeBillingGate from "../StripeBillingGate";

// Security constants
const MAX_NAME_LENGTH = 100;
const MAX_EMAIL_LENGTH = 254;
const MAX_PHONE_LENGTH = 20;
const SUBMISSION_COOLDOWN = 1000; // 1 second between API calls

// Input sanitization function
const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // Remove dangerous patterns
  sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
  sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
  sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
  sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
  sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
  
  return sanitized.trim();
};

// Validate URL parameters
const validateStudioId = (studioId: string | null): boolean => {
  if (!studioId || typeof studioId !== 'string') return false;
  // UUID format validation
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(studioId);
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

// Data masking for sensitive information
const maskEmail = (email: string): string => {
  if (!email || email.length < 3) return email;
  const [localPart, domain] = email.split('@');
  if (!domain) return email;
  const maskedLocal = localPart.length > 2 
    ? localPart.substring(0, 2) + '*'.repeat(Math.max(0, localPart.length - 2))
    : localPart;
  return `${maskedLocal}@${domain}`;
};

const maskPhone = (phone: string): string => {
  if (!phone || phone.length < 4) return phone;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 4) return phone;
  return phone.substring(0, phone.length - 4) + '****';
};

type OverdueStudent = {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  overdue_amount: number;
  payment_type?: 'stripe' | 'manual';
};

export default function OverduePaymentsCard({
  studioId,
  isStripeConnected,
  canUseStripeBilling = true,
}: {
  studioId: string | null;
  isStripeConnected: boolean | null;
  canUseStripeBilling?: boolean;
}) {
  const { getAuthToken } = useAuth();
  const { ref, inView } = useInView();
  const [loading, setLoading] = useState(true);
  const [studentsWithOverdue, setStudentsWithOverdue] = useState<OverdueStudent[]>([]);
  const [lastPortalTime, setLastPortalTime] = useState<number>(0);
  const [fetchGeneration, setFetchGeneration] = useState(0);
  const wasInViewRef = useRef(false);
  const lastFetchTimeRef = useRef(0);

  const handleGenerateCustomerPortalLink = async (student: OverdueStudent) => {
    const token = getAuthToken();
    if (!token) return;

    // Rate limiting for portal link generation
    const now = Date.now();
    if (now - lastPortalTime < SUBMISSION_COOLDOWN) {
      logSecurityEvent('PORTAL_RATE_LIMIT_EXCEEDED', { studentId: student.id });
      return;
    }

    // Validate student ID
    if (!student.id || typeof student.id !== 'string') {
      logSecurityEvent('INVALID_STUDENT_ID', { studentId: student.id });
      return;
    }

    setLastPortalTime(now);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payment/link`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            student_id: student.id,
          }),
        }
      );

      if (response.ok) {
        const { url } = await response.json();
        // Validate the URL before opening
        if (url && typeof url === 'string' && url.startsWith('https://')) {
          window.open(url, '_blank');
        } else {
          logSecurityEvent('INVALID_PORTAL_URL', { url });
        }
      } else {
        logSecurityEvent('PORTAL_REQUEST_FAILED', { 
          status: response.status, 
          statusText: response.statusText,
          studentId: student.id
        });
      }
    } catch (error) {
      logSecurityEvent('PORTAL_REQUEST_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        studentId: student.id
      });
    }
  };

  useEffect(() => {
    if (!canUseStripeBilling || isStripeConnected === false) {
      setLoading(false);
    }
  }, [canUseStripeBilling, isStripeConnected]);

  useEffect(() => {
    if (!inView) {
      wasInViewRef.current = false;
      return;
    }

    if (!wasInViewRef.current) {
      wasInViewRef.current = true;
      setFetchGeneration((value) => value + 1);
    }
  }, [inView]);

  useEffect(() => {
    const fetchData = async () => {
      if (
        !inView ||
        !studioId ||
        !canUseStripeBilling ||
        !isStripeConnected
      ) {
        return;
      }
      
      // Security validations
      if (!validateStudioId(studioId)) {
        logSecurityEvent('INVALID_STUDIO_ID', { studioId });
        return;
      }
      
      // Rate limiting
      const now = Date.now();
      if (now - lastFetchTimeRef.current < SUBMISSION_COOLDOWN) {
        return;
      }
      
      const token = getAuthToken();
      if (!token) return;
      
      setLoading(true);
      lastFetchTimeRef.current = now;
      
      try {
        // Use encodeURIComponent for URL parameters
        const encodedStudioId = encodeURIComponent(studioId);
        
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/students/${encodedStudioId}/overdue`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (res.ok) {
          const json = await res.json();
          // Sanitize the student data
          const sanitizedStudents = (json || []).map((student: OverdueStudent) => ({
            ...student,
            name: sanitizeInput(student.name).substring(0, MAX_NAME_LENGTH),
            email: student.email ? sanitizeInput(student.email).substring(0, MAX_EMAIL_LENGTH) : undefined,
            phone: student.phone ? sanitizeInput(student.phone).substring(0, MAX_PHONE_LENGTH) : undefined
          }));
          setStudentsWithOverdue(sanitizedStudents);
        } else {
          logSecurityEvent('API_REQUEST_FAILED', { 
            status: res.status, 
            statusText: res.statusText,
            endpoint: 'students/overdue'
          });
          setStudentsWithOverdue([]);
        }
      } catch (error) {
        logSecurityEvent('API_REQUEST_ERROR', { 
          error: error instanceof Error ? error.message : 'Unknown error',
          endpoint: 'students/overdue'
        });
        setStudentsWithOverdue([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [inView, studioId, canUseStripeBilling, isStripeConnected, getAuthToken, fetchGeneration]);

  return (
    <div ref={ref} className="relative overflow-hidden bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-gray-800 border-2 border-red-100 dark:border-red-800 shadow-md rounded-xl px-4 py-5 sm:px-6 sm:py-6 hover:shadow-lg hover:-translate-y-1 transition">
      <div>
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold font-sans leading-6 text-gray-900 dark:text-white">Students With Outstanding Payments</h3>
          <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-bold px-2 py-1 rounded">Requires Attention</span>
        </div>
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Stripe open invoices plus family billing primaries marked past due
        </div>
        <div className="mt-2">
          {(!inView || loading) &&
          canUseStripeBilling &&
          isStripeConnected &&
          !studentsWithOverdue.length ? (
            <div className="flex justify-center items-center py-4">
              <div className="animate-pulse flex space-x-4 items-center">
                <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Loading overdue payments...</div>
              </div>
            </div>
          ) : isStripeConnected === null && canUseStripeBilling ? (
            <div className="flex justify-center items-center py-4">
              <div className="animate-pulse flex space-x-4 items-center">
                <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Preparing payment data...</div>
              </div>
            </div>
          ) : !canUseStripeBilling || isStripeConnected === false ? (
            <StripeBillingGate
              canUseStripeBilling={canUseStripeBilling}
              isStripeConnected={isStripeConnected}
              variant="card"
            />
          ) : studentsWithOverdue.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No students with overdue payments.</p>
          ) : (
            <div className="overflow-x-auto overflow-y-hidden -mx-2 px-2 xl:mx-0 xl:px-0" style={{ scrollbarWidth: 'thin' }}>
              <div className="min-w-[600px] xl:min-w-0">
            <div className="relative overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                <thead>
                  <tr>
                    <th className="py-2 pl-4 pr-3 text-left font-bold font-sans text-gray-900 dark:text-white sm:pl-6 w-1/4">Name</th>
                    <th className="px-3 py-2 text-left font-bold font-sans text-gray-900 dark:text-white w-1/4">Contact</th>
                    <th className="px-3 py-2 text-left font-bold font-sans text-gray-900 dark:text-white w-1/4">Amount Owed</th>
                    <th className="px-3 py-2 text-left font-bold font-sans text-gray-900 dark:text-white w-1/4">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {studentsWithOverdue.slice(0, 5).map((student) => (
                    <tr key={student.id} className="align-middle">
                      <td className="whitespace-nowrap py-2 pl-4 pr-3 text-xs font-medium text-gray-900 dark:text-white sm:pl-6">
                        <div className="flex items-center gap-2">
                          <UserIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                          <span>{student.name}</span>
                          {student.payment_type === 'manual' ? (
                            <span className="px-1.5 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
                              💵 Cash
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
                              💳 Auto
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1">
                            <EnvelopeIcon className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                            {student.email ? (
                              <span title="Click portal to access full contact info">{maskEmail(student.email)}</span>
                            ) : (
                              <span className="italic text-gray-300 dark:text-gray-600">No email</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <PhoneIcon className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                            {student.phone ? (
                              <span title="Click portal to access full contact info">{maskPhone(student.phone)}</span>
                            ) : (
                              <span className="italic text-gray-300 dark:text-gray-600">No phone</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-xs">
                        <span className="font-medium text-red-600 dark:text-red-400">${student.overdue_amount.toFixed(2)}</span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-xs">
                        {student.payment_type === 'manual' ? (
                          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                            Manual payment
                          </span>
                        ) : (
                          <button
                            onClick={() => handleGenerateCustomerPortalLink(student)}
                            className="text-xs bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-200 px-2 py-1 rounded hover:bg-amber-200 dark:hover:bg-amber-700 transition-colors"
                          >
                            Portal
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {studentsWithOverdue.length > 5 && (
                <div className="flex justify-center items-center mt-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Showing first 5 of {studentsWithOverdue.length} students</span>
                </div>
              )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

